import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';

const MAX_ACTION_STACK_SIZE = 100;

type UndoAction = {
    undo: () => void | Promise<void>;
    redo?: () => void | Promise<void>;
};

@Injectable({
    providedIn: 'root'
})
export class UndoService {
    private actionIndex = -1;
    private actionStack: UndoAction[] = [];

    private canUndoSubscribers: Subscriber<boolean>[] = [];
    private canRedoSubscribers: Subscriber<boolean>[] = [];

    constructor() { }

    public get canUndoChange(): Observable<boolean> {
        return new Observable<boolean>(sub => {
            this.canUndoSubscribers.push(sub);
            return () => { this.canUndoSubscribers.splice(this.canUndoSubscribers.indexOf(sub), 1); };
        });
    }

    public get canRedoChange(): Observable<boolean> {
        return new Observable<boolean>(sub => {
            this.canRedoSubscribers.push(sub);
            return () => { this.canRedoSubscribers.splice(this.canRedoSubscribers.indexOf(sub), 1); };
        });
    }

    public get canUndo(): boolean { 
        return this.actionStack.length > 0 && this.actionIndex > -1;
    }

    public get canRedo(): boolean {
        return this.actionStack.length > 0 && this.actionIndex < this.actionStack.length - 1 && !!this.actionStack[Math.max(this.actionIndex, 0)]?.redo;
    }

    public clear() {
        this.actionIndex = -1;
        this.actionStack = [];
    }

    public async addUndoRedo(undo: () => void | Promise<void>, redo?: () => void | Promise<void>): Promise<void> {
        // Push onto action stack
        this.actionStack.splice(this.actionIndex + 1, this.actionStack.length - 1 - this.actionIndex, {
            undo, redo
        });

        // Set action index to last item
        this.actionIndex = this.actionStack.length - 1;

        // If action stack exceeds max size, remove furthes back action
        if (this.actionStack.length > MAX_ACTION_STACK_SIZE) {
            // Remove furthest back item
            this.actionStack.shift();

            // Lowered active action index
            this.actionIndex = Math.max(0, Math.min(this.actionStack.length - 1, this.actionIndex - 1));
        }

        // Trigger can undo change
        this.canUndoSubscribers.forEach(sub => sub.next(this.canUndo));
    }

    public async undo(): Promise<void> {
        // console.log('UNDO')
        if (this.actionStack.length == 0) return; // Make sure there are things on the action stack
        if (this.actionIndex == -1) return; // Make sure we have things to undo

        // Get relevant undo action
        const undoAction = this.actionStack[this.actionIndex].undo;

        // Lower active action index
        this.actionIndex = Math.max(-1, Math.min(this.actionStack.length - 1, this.actionIndex - 1));

        // Perform undo and await if needed
        const possiblePromise = undoAction();
        if (possiblePromise)
            await possiblePromise;

        // Check if we can still undo after
        if (!this.canUndo)
            this.canUndoSubscribers.forEach(sub => sub.next(this.canUndo));

        // Trigger can redo change
        this.canRedoSubscribers.forEach(sub => sub.next(this.canRedo));
    }

    public async redo(): Promise<void> {
        // console.log('REDO')
        if (this.actionStack.length == 0) return; // Make sure there are things on the action stack
        if (this.actionIndex === this.actionStack.length - 1) return; // Make sure we have anything to redo

        // Get relevant redo action
        const redoAction = this.actionStack[this.actionIndex + 1].redo;

        // Increase active action index
        this.actionIndex = Math.max(-1, Math.min(this.actionStack.length - 1, this.actionIndex + 1));

        // Perform redo and await if needed
        const possiblePromise = redoAction();
        if (possiblePromise)
            await possiblePromise;

        // Check if we can still redo after
        if (!this.canRedo)
            this.canRedoSubscribers.forEach(sub => sub.next(this.canRedo));

        // Trigger can undo change
        this.canUndoSubscribers.forEach(sub => sub.next(this.canUndo));
    }
}
